import React from 'react';
import { toast } from 'react-toastify';
import {
  CallReceivedIcon,
  CallSharedIcon,
  SMSReceivedIcon,
  SMSSharedIcon,
  SpeechBubbleIcon,
} from '@rsos/assets/icons';
import { Box, Text } from '@rsos/components/capstone/base';
import { SOURCE_SMS } from '@rsos/constants/locationSources';

export const TOAST_EXPIRATION_TIME = 1000 * 30;

const toastOptions = {
  autoClose: TOAST_EXPIRATION_TIME,
  bodyClassName: 'Toastify__overwrite-body',
  closeButton: true,
  closeOnClick: true,
  draggable: false,
  pauseOnFocusLoss: false,
  pauseOnHover: false,
  position: 'top-center',
};

/**
 * purple toast notification - a call or sms event has been received by the peer agency
 *
 * @param {string} agencyName - sharer's agency name
 * @param {string} callerID - caller id that is shared
 * @param {string} callSource - call source - `call` or `sms`
 * @param {function} onClick - called when click inside the toast notification
 * @param {function} onClose - called when the toast notification closes
 * @param {object} theme - styled component theme object
 */
export const toastReceived = (
  agencyName,
  callerID,
  callSource,
  onClick,
  onClose,
  theme,
) => {
  const toastMessage = (
    <Box align="center" color={theme.colors.background} flow={10} horizontal>
      {callSource === SOURCE_SMS ? (
        <SMSReceivedIcon width={24} height={24} />
      ) : (
        <CallReceivedIcon height={14} width={16} />
      )}
      <Box width="205px">
        <Text ellipsis size="normal">
          {agencyName} sent you an event
        </Text>
      </Box>
    </Box>
  );

  toast(toastMessage, {
    ...toastOptions,
    onClick: () => onClick(),
    onClose: () => onClose(),
    className: 'Toastify__agency-share-chat-toast-received',
    toastId: `toastReceived-${callerID}`,
  });
};

/**
 * green toast notification - a call or sms event has been shared with the peer agency
 *
 * @param {string} agencyName - receiver's agency name
 * @param {string} callSource - call source - `call` or `sms`
 * @param {object} theme - styled component theme object
 */
export const toastShared = (agencyName, callSource) => {
  const toastMessage = (
    <Box align="center" color="background" flow={10} horizontal>
      {callSource === SOURCE_SMS ? (
        <SMSSharedIcon height={24} width={25} />
      ) : (
        <CallSharedIcon height={13} width={13} />
      )}
      <Box width="205px">
        <Text ellipsis size="normal">
          You sent an event to {agencyName}
        </Text>
      </Box>
    </Box>
  );

  toast(toastMessage, {
    ...toastOptions,
    className: 'Toastify__agency-share-chat-toast-shared',
  });
};

/**
 * blue toast notification - an unread call or sms event for participating agencies
 *
 * @param {number} numUnreads - number of unreads
 * @param {object} theme - styled component theme object
 */
export const toastUnread = (numUnreads, theme) => {
  const toastMessage = (
    <Box align="center" color={theme.colors.background} flow={10} horizontal>
      <SpeechBubbleIcon height={16} width={16} />
      <Box width="205px">
        <Text ellipsis size="normal">
          You have {numUnreads} new conversation{numUnreads > 1 ? 's' : ''}
        </Text>
      </Box>
    </Box>
  );

  if (!toast.isActive('toastUnread')) {
    toast(toastMessage, {
      ...toastOptions,
      className: 'Toastify__agency-share-chat-toast-unread',
      toastId: 'toastUnread',
    });
  } else {
    toast.update('toastUnread', {
      render: toastMessage,
    });
  }
};
